/**
 * Eli, 17 Oct 2026: Volunteer help is for Events or Other only. Events → VolunteerHelpEvents (pick a posted event,
 * how many, email everyone Ready). Other → what and where → when (5-minute steps) → how many, and email every Ready
 * volunteer (or assign one already arranged) → check and book. The service choice is gone: Other goes to all
 * volunteers (shared/activities.ts ALL_VOLUNTEERS).
 *
 * Book → Volunteer help (1.8.3, D-1.8.3-5): volunteer help with no tenant (a tenant may be named) — pick the service,
 * see the volunteers who have it ticked (Ready first), then Assign one already arranged or Send to all of them.
 *
 * One question per screen (docs/ONE-TAB-ONE-JOB.md): the service and who has it → what and where → when and how
 * many → who does it (Assign / Send) → check and book. It is built on the 1.5.1 activities: the press makes an
 * activity and either confirms one volunteer at once (one confirmation email, waiting in the Outbox) or sends its
 * first date to every Ready volunteer with the service (the count stops at the number needed; whoever answers after
 * that is asked the standby question). The activities already there — and the ones made here — live on Events, under
 * Event & activity support: this sheet lists them and links there. Nothing already sent is sent again.
 *
 * The props are final: BookingProvider mounts it for `program: 'volunteer-help'` inside its <Sheet>.
 */
import { useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { BookProgram } from '@shared/book-programs';
import { addDaysToKey, dayOfWeek } from '@shared/dates';
import { lengthWords } from '@shared/dayplan';
import { AlmostLine } from './Ready171';
import { ACTIVITY_CAP_MAX, ALL_VOLUNTEERS, activityDates, type HelpPickerView, type VolunteerHelpInput, type VolunteerHelpResult } from '@shared/activities';
import { minutesLabel } from '@shared/volunteers';
import { apiFetch } from '@/lib/api';
import { formatDayMedium } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StepHead, SummaryCard } from '@/components/Steps';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { SelectField, TextArea, TextField } from '@/features/people/fields';
import { ProgramPicker } from '@/features/booking/ProgramPicker';
import { postJson, useActivities, useActivityRefresh } from '@/features/activities/api';
import { activityWhen } from '@/features/activities/ActivitiesSection';
import { weekdayName } from '@/features/activities/NewActivitySheet';
import { serviceLabel, useAddedServices } from './Services183';
import { TimeSelect } from '@/components/TimeSelect';
import { VolunteerHelpEvents } from './VolunteerHelpEvents';
import { SpanishWording, useSpanishPeople } from '@/features/messages/SpanishWording';

export interface VolunteerHelpSheetProps {
  /** A tenant already chosen on Book (may be named on the help; never required). */
  tenant?: { tenantId: string; unit: string; name: string } | null;
  todayKey: string;
  onDone: () => void;
  /** Book's "what" row: another program was pressed. */
  onProgram?: (program: BookProgram) => void;
  returnFocusTo?: RefObject<HTMLElement | null>;
}

const STEPS = ['type', 'what', 'when', 'how', 'confirm'] as const;
type Step = (typeof STEPS)[number];
const LENGTHS = Array.from({ length: 16 }, (_, i) => 30 + i * 30); // 30 min – 8 h
const iso = (k: string) => `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}`;

export function useHelpPicker(service: string | null) {
  return useQuery({ queryKey: ['help-picker', service], queryFn: () => apiFetch<HelpPickerView>(`/api/activities/help/volunteers?service=${encodeURIComponent(service ?? '')}`), enabled: !!service, staleTime: 10_000 });
}

export function VolunteerHelpSheet({ tenant, todayKey, onDone, onProgram, returnFocusTo }: VolunteerHelpSheetProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useActivityRefresh();
  const added = useAddedServices();
  const existing = useActivities();
  const [step, setStep] = useState<Step>('type');
  const [mode, setMode] = useState<'events' | 'other' | null>(null);
  const service: string | null = mode === 'other' ? ALL_VOLUNTEERS : null;
  const [name, setName] = useState('');
  const [details, setDetails] = useState('');
  // D-1101-SMALL-1: the Spanish wording of the details (or of the name when there are none); the app never translates
  const spanishPeople = useSpanishPeople();
  const [detailsEs, setDetailsEs] = useState('');
  const [where, setWhere] = useState('');
  const [kind, setKind] = useState<'once' | 'weekly'>('once');
  const [day, setDay] = useState(iso(addDaysToKey(todayKey, 1)));
  const [weekday, setWeekday] = useState(6);
  const [until, setUntil] = useState('');
  const [minutes, setMinutes] = useState(10 * 60);
  const [duration, setDuration] = useState(60);
  const [needed, setNeeded] = useState('1');
  const [how, setHow] = useState<'assign' | 'send'>('send');
  const [assignee, setAssignee] = useState('');
  const [nameTenant, setNameTenant] = useState(!!tenant);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const picker = useHelpPicker(service);

  const list = added.data?.services ?? [];
  const label = (k: string) => (k === ALL_VOLUNTEERS ? t('vb.h.allVols') : serviceLabel(k, t, lang, list));
  const n = STEPS.indexOf(step) + 1;
  const neededN = Math.min(ACTIVITY_CAP_MAX, Math.max(1, Math.floor(Number(needed.replace(/\D/g, '')) || 0)));
  const dayKey = day.replace(/-/g, '');
  const vols = picker.data?.volunteers ?? [];
  const ready = vols.filter((v) => v.ready);
  const chosen = ready.find((v) => v.volunteerId === assignee) ?? null;
  const first = (s: string) => s.replace(/^SECURITY-TEST\s+/, '').split(' ')[0] || s;
  const firstDay = kind === 'once' ? dayKey : (activityDates({ kind: 'weekly', weekday, fromDayKey: todayKey, untilDayKey: until ? until.replace(/-/g, '') : null }, todayKey, 1)[0] ?? '');
  const whenWords =
    kind === 'weekly'
      ? t('activities.everyWeekday', { weekday: weekdayName(weekday, lang), time: minutesLabel(minutes), length: lengthWords(duration) })
      : t('activities.onDay', { day: /^\d{8}$/.test(dayKey) ? formatDayMedium(dayKey, lang) : '—', time: minutesLabel(minutes), length: lengthWords(duration) });

  const stop = (m: string) => {
    setProblem(m);
    announce(m, 'assertive');
  };
  const go = (s: Step) => {
    setProblem(null);
    setStep(s);
  };
  const next = () => {
    if (step === 'type') return mode === 'other' ? go('what') : stop(t('vb.h.needType'));
    if (step === 'what') {
      if (!name.trim()) return stop(t('svc183.help.needName'));
      if (!where.trim()) return stop(t('svc183.help.needWhere'));
      return go('when');
    }
    if (step === 'when') {
      if (kind === 'once' && !/^\d{8}$/.test(dayKey)) return stop(t('svc183.help.needDate'));
      if (kind === 'once' && dayKey < todayKey) return stop(t('svc183.help.pastDate'));
      return go('how');
    }
    if (step === 'how') {
      if (how === 'assign' && !chosen) return stop(t('svc183.help.needAssignee'));
      if (how === 'send' && !ready.length) return stop(t('svc183.help.sendNobody', { service: service ? label(service) : '' }));
      return go('confirm');
    }
  };
  const back = () => go(STEPS[Math.max(0, n - 2)]!);

  const save = async () => {
    if (!service) return;
    const body: VolunteerHelpInput = {
      service,
      name: name.trim(),
      details: details.trim(),
      detailsEs: detailsEs.trim(),
      where: where.trim(),
      schedule: kind === 'weekly' ? { kind: 'weekly', weekday, fromDayKey: todayKey, untilDayKey: until ? until.replace(/-/g, '') : null } : { kind: 'once', dayKey },
      minutes,
      duration,
      needed: neededN,
      tenant: tenant && nameTenant ? { tenantId: tenant.tenantId, unit: tenant.unit, name: tenant.name } : null,
      how: how === 'assign' ? { kind: 'assign', volunteerId: assignee } : { kind: 'send' },
    };
    setBusy(true);
    setProblem(null);
    try {
      const r = await postJson<VolunteerHelpResult>('/api/activities/help', body);
      toast(r.message);
      announce(r.message);
      refresh();
      onDone();
    } catch (e) {
      stop(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'events') return <VolunteerHelpEvents onDone={onDone} onBack={() => setMode(null)} onProgram={onProgram} returnFocusTo={returnFocusTo} />;

  let body: React.ReactNode = null;
  if (step === 'type') {
    const acts = existing.data?.activities ?? [];
    body = (
      <>
        <div className="cat-cards book183-plans" role="group" aria-labelledby="vh-q" data-testid="vh-type">
          <button type="button" aria-pressed={false} onClick={() => setMode('events')} data-testid="vh-type-events">
            <b>{t('vb.h.events')}</b>
            <small>{t('vb.h.eventsSub')}</small>
          </button>
          <button
            type="button"
            aria-pressed={mode === 'other'}
            onClick={() => {
              setMode('other');
              setAssignee('');
              go('what');
            }}
            data-testid="vh-type-other"
          >
            <b>{t('vb.h.other')}</b>
            <small>{t('vb.h.otherSub')}</small>
          </button>
        </div>
        <section className="svc183-existing" aria-labelledby="vh-existing-h" data-testid="vh-existing">
          <h4 className="eyebrow" id="vh-existing-h">
            {t('svc183.help.existingTitle')}
          </h4>
          {existing.data && !acts.length ? <p className="help calm">{t('svc183.help.existingNone')}</p> : null}
          {acts.length ? (
            <ul className="svc183-acts">
              {acts.map((a) => (
                <li key={a.activityId} data-testid={`vh-existing-${a.activityId}`}>
                  {t('svc183.help.existingLine', { name: a.name, when: activityWhen(a, t, lang), confirmed: a.dates.find((d) => d.dayKey >= todayKey)?.confirmed ?? 0, needed: a.cap })}
                </li>
              ))}
            </ul>
          ) : null}
          <p>
            <Link to="/events#h-activities" onClick={onDone} data-testid="vh-existing-open">
              {t('svc183.help.existingOpen')}
            </Link>
          </p>
        </section>
      </>
    );
  } else if (step === 'what') {
    body = (
      <>
        <TextField id="vh-name" label={t('svc183.help.name')} required value={name} onChange={setName} placeholder={t('svc183.help.namePh')} help={t('svc183.help.nameHelp')} maxLength={120} />
        <TextArea id="vh-details" label={t('svc183.help.details')} value={details} onChange={setDetails} maxLength={1000} />
        <SpanishWording of={t('svc183.help.details')} count={spanishPeople} value={detailsEs} onChange={setDetailsEs} rows={3} maxLength={1000} testId="vh-details-es" />
        <TextField id="vh-where" label={t('svc183.help.where')} required value={where} onChange={setWhere} placeholder={t('svc183.help.wherePh')} maxLength={255} />
      </>
    );
  } else if (step === 'when') {
    body = (
      <>
        <fieldset className="field">
          <legend className="eyebrow">{t('svc183.help.howOften')}</legend>
          <div className="seg" role="group" aria-label={t('svc183.help.howOften')}>
            <button type="button" aria-pressed={kind === 'once'} onClick={() => setKind('once')} data-testid="vh-once">
              {t('svc183.help.once')}
            </button>
            <button type="button" aria-pressed={kind === 'weekly'} onClick={() => setKind('weekly')} data-testid="vh-weekly">
              {t('svc183.help.weekly')}
            </button>
          </div>
        </fieldset>
        {kind === 'once' ? (
          <TextField id="vh-date" label={t('svc183.help.date')} type="date" required value={day} onChange={setDay} help={/^\d{8}$/.test(dayKey) ? weekdayName(dayOfWeek(dayKey), lang) : undefined} />
        ) : (
          <div className="grid-2">
            <SelectField id="vh-weekday" label={t('svc183.help.weekday')} value={String(weekday)} onChange={(v) => setWeekday(Number(v))} options={[0, 1, 2, 3, 4, 5, 6].map((d) => ({ value: String(d), label: weekdayName(d, lang) }))} />
            <TextField id="vh-until" label={t('svc183.help.until')} type="date" value={until} onChange={setUntil} />
          </div>
        )}
        <div className="grid-2">
          <TimeSelect id="vh-time" label={t('svc183.help.time')} value={minutes} onChange={setMinutes} />
          <SelectField id="vh-length" label={t('svc183.help.length')} value={String(duration)} onChange={(v) => setDuration(Number(v))} options={LENGTHS.map((m) => ({ value: String(m), label: lengthWords(m) }))} />
        </div>
        <TextField id="vh-needed" label={t('svc183.help.needed')} required inputMode="numeric" value={needed} onChange={setNeeded} help={t('svc183.help.neededHelp', { max: ACTIVITY_CAP_MAX })} />
        <p className="help">{t('svc183.help.weekends')}</p>
      </>
    );
  } else if (step === 'how') {
    body = (
      <>
        <fieldset className="field choice">
          <legend>{t('svc183.help.howLegend')}</legend>
          <div className="seg radios svc183-how">
            <label className={how === 'assign' ? 'on' : undefined}>
              <input type="radio" name="vh-how" value="assign" checked={how === 'assign'} onChange={() => setHow('assign')} data-testid="vh-how-assign" />
              <span>{t('svc183.help.assign')}</span>
            </label>
            <label className={how === 'send' ? 'on' : undefined}>
              <input type="radio" name="vh-how" value="send" checked={how === 'send'} onChange={() => setHow('send')} data-testid="vh-how-send" />
              <span>{t('svc183.help.send')}</span>
            </label>
          </div>
        </fieldset>
        {how === 'assign' ? (
          <>
            <SelectField id="vh-assignee" label={t('svc183.help.assignee')} required value={assignee} onChange={setAssignee} options={[{ value: '', label: t('svc183.help.assigneePick') }, ...ready.map((v) => ({ value: v.volunteerId, label: v.name }))]} />
            {!ready.length ? (
              <p className="help" role="note" data-testid="vh-none-ready">
                {t('svc183.help.nobodyReady', { service: service ? label(service) : '' })}
              </p>
            ) : null}
            {chosen ? (
              <p className="help" data-testid="vh-assign-note">
                {chosen.hasEmail ? t('svc183.help.assignNote', { name: first(chosen.name) }) : t('svc183.help.assignNoEmail', { name: first(chosen.name) })}
              </p>
            ) : null}
          </>
        ) : (
          <div data-testid="vh-send-note">
            {ready.length ? (
              <>
                <p className="help">{t('svc183.help.sendNote', { count: ready.length })}</p>
                <p className="help">{t('svc183.help.capNote', { count: neededN })}</p>
              </>
            ) : (
              <p className="help" role="note">
                {t('svc183.help.sendNobody', { service: service ? label(service) : '' })}
              </p>
            )}
          </div>
        )}
        {/* Eli, 21 Oct: the not-Ready line, as on Request a volunteer — staff screen only */}
        <AlmostLine almost={picker.data?.almost} service={null} testId="help-almost" />
        <fieldset className="field svc183-tenant">
          <legend className="eyebrow">{t('svc183.help.tenantLegend')}</legend>
          {tenant ? (
            <label className="switch" htmlFor="vh-tenant">
              <input id="vh-tenant" type="checkbox" checked={nameTenant} onChange={(e) => setNameTenant(e.target.checked)} data-testid="vh-tenant" />
              <span>{t('svc183.help.tenantName', { name: tenant.name, unit: tenant.unit })}</span>
            </label>
          ) : null}
          <p className="help">{tenant ? t('svc183.help.tenantHelp') : t('svc183.help.tenantNone')}</p>
        </fieldset>
      </>
    );
  } else {
    body = (
      <SummaryCard
        testId="vh-confirm"
        rows={[
          { label: t('svc183.help.c.service'), value: service ? label(service) : '' },
          { label: t('svc183.help.c.what'), value: name.trim() },
          ...(details.trim() ? [{ label: t('svc183.help.c.details'), value: details.trim() }] : []),
          { label: t('svc183.help.c.where'), value: where.trim() },
          { label: t('svc183.help.c.when'), value: whenWords },
          { label: t('svc183.help.c.needed'), value: t('svc183.help.neededWords', { count: neededN }) },
          { label: t('svc183.help.c.how'), value: how === 'assign' ? t('svc183.help.howAssign', { name: chosen?.name ?? '' }) : t('svc183.help.howSend', { count: ready.length }) },
          ...(tenant && nameTenant ? [{ label: t('svc183.help.c.tenant'), value: `${tenant.name} (${tenant.unit})` }] : []),
        ]}
      >
        {how === 'assign' && chosen ? <p className="help">{chosen.hasEmail ? t('svc183.help.assignNote', { name: first(chosen.name) }) : t('svc183.help.assignNoEmail', { name: first(chosen.name) })}</p> : null}
        {how === 'send' ? <p className="help">{t('svc183.help.capNote', { count: neededN })}</p> : null}
        {spanishPeople && spanishPeople > 0 ? (
          <p className="help" data-testid="vh-spanish-line">
            {detailsEs.trim() ? t('oneLang.helpLineGiven', { count: spanishPeople }) : t('oneLang.helpLineBlank', { count: spanishPeople })}
          </p>
        ) : null}
        {kind === 'weekly' && firstDay ? <p className="help">{t('svc183.help.weeklyNote', { day: formatDayMedium(firstDay, lang) })}</p> : null}
        <p className="help">{t('svc183.help.frontDesk')}</p>
        <p className="help">{t('svc183.help.hours')}</p>
      </SummaryCard>
    );
  }

  return (
    <SheetContent
      title={t('book183.program.volunteer-help')}
      wide
      returnFocusTo={returnFocusTo}
      data-testid="volunteer-help-sheet"
      footer={
        <>
          {step === 'type' ? (
            <Button variant="secondary" type="button" onClick={onDone} data-testid="vh-cancel">
              {t('svc183.help.cancel')}
            </Button>
          ) : (
            <Button variant="secondary" type="button" onClick={back} data-testid="vh-back">
              {t('svc183.help.back')}
            </Button>
          )}
          {step === 'confirm' ? (
            <Button type="button" disabled={busy} onClick={() => save()} data-testid="vh-save">
              {how === 'send' ? t('svc183.help.saveSend') : chosen?.hasEmail ? t('svc183.help.saveAssign') : t('svc183.help.saveAssignNoEmail')}
            </Button>
          ) : step === 'type' ? null : (
            <Button type="button" onClick={next} data-testid="vh-next">
              {t('svc183.help.next')}
            </Button>
          )}
        </>
      }
    >
      {step === 'type' && onProgram ? <ProgramPicker value="volunteer-help" onChange={onProgram} /> : null}
      <StepHead n={n} of={STEPS.length} question={step === 'type' ? t('vb.h.q.type') : t(`svc183.help.q.${step}`)} id="vh-q" testId="vh-question" />
      {body}
      {problem ? (
        <p className="field-err" role="alert" data-testid="vh-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
